/* eslint-disable global-require */
import _ from 'lodash';
import { createStore, applyMiddleware, compose } from 'redux';
import { combineReducers } from 'redux-immutablejs';
import { createEpicMiddleware, combineEpics } from 'redux-observable';
import thunk from 'redux-thunk';
import { BehaviorSubject } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import BCGoogleAnalyticsUtils from '@bibliocommons/utils-google-analytics';
import airbrakeMiddleware from '../../reduxMiddlewares/airbrakeMiddleware';
import apiRequestMiddleware from '../../reduxMiddlewares/apiRequestMiddleware';
import authMiddleware from '../../reduxMiddlewares/authMiddleware';
import redirectMiddleware from '../../reduxMiddlewares/redirectMiddleware';
import gaMiddleware from '../../reduxMiddlewares/gaMiddleware';
import analyticsMiddleware from '../../reduxMiddlewares/analyticsMiddleware';

/**
 * @param  {object} initialState - initial state (@see redux.createStore(), second argument)
 * @param  {object} apiClient - we inject the httpClient for easier mocking
 *                            plus easily changing http library on client and server.
 *                            (note - this will change with webpack build)
 * @param  {boolean} enableWebpackHMR - whether to enable hot module reloading (@see config)
 *
 * @return {object} - redux stored with all the initial data loaded
 */
export default function createReduxStore(
  initialState,
  apiClient,
  enableWebpackHMR = false,
  enableReduxDevTools = false
) {
  const rootEpic = (...args) => combineEpics(require('app/epics/itemLevelHoldExceptionEpic').default)(...args, apiClient);
  const epicMiddleware = createEpicMiddleware();
  const middlewares = [
    authMiddleware,
    redirectMiddleware,
    apiRequestMiddleware(apiClient),
    epicMiddleware,
    thunk.withExtraArgument(apiClient),
    airbrakeMiddleware(),
    gaMiddleware(BCGoogleAnalyticsUtils),
    analyticsMiddleware()
  ];

  let combinedReducers = combineReducers(require('../../reducers').default);
  const lastCreatedStore = createStore(
    combinedReducers,
    initialState,
    compose(
      applyMiddleware(..._.compact(middlewares)),
      enableReduxDevTools && window.__REDUX_DEVTOOLS_EXTENSION__ ? window.__REDUX_DEVTOOLS_EXTENSION__() : f => f
    )
  );

  const epic$ = new BehaviorSubject(rootEpic);
  // Every time a new epic is given to epic$ it will unsubscribe from the 
  // previous one then call and subscribe to the new one because of how switchMap works
  const hotReloadingEpic = (...args) =>
  epic$.pipe(
    switchMap(epic => epic(...args))
  );

  epicMiddleware.run(hotReloadingEpic);

  if (enableWebpackHMR && module.hot) {
    module.hot.accept('../../reducers', () => {
      combinedReducers = combineReducers(require('../../reducers').default);
      const nextRootEpic = combineEpics(..._.values(require('../../epics')));
      lastCreatedStore.replaceReducer(combinedReducers);
      epic$.next(nextRootEpic);
    });
  }

  return lastCreatedStore;
}
